import { useDeleteCollection, useFetchCollectionImageList, useFetchCollectionName, usePutImageUse } from '@api';
import { PathnameEnum, QueryParamKeyEnum, ToastMessageEnum } from '@constant';
import { useQueryClient } from '@tanstack/react-query';
import { IImageListItem } from '@types';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useToast } from './useToast';

export const useCollectionImageList = () => {
  const { collectionIdPathParam } = useParams(); // URL 파라미터에서 collectionId 추출

  // 컬렉션 생성/수정 모달 관련
  const [isCollectionUpsertModalVisible, setIsCollectionUpsertModalVisible] = useState<boolean>(false);
  // 컬렉션 삭제 관련
  const [isCollectionDeleteModalVisible, setIsCollectionDeleteModalVisible] = useState<boolean>(false);

  const { showToast } = useToast();

  const navigate = useNavigate();
  const outputBottomRef = useRef<HTMLDivElement | null>(null);
  const outputBottomRefCurrent = outputBottomRef.current;
  const location = useLocation();
  const locationPathname = location.pathname;
  const queryClient = useQueryClient();

  const { data: collectionNameData } = useFetchCollectionName(Number(collectionIdPathParam));
  const initialCollectionName = collectionNameData?.name;

  const { data, hasNextPage, fetchNextPage, isLoading, isFetching } = useFetchCollectionImageList(
    Number(collectionIdPathParam)
  );

  const { mutateAsync: putImageUseMutateAsync } = usePutImageUse((err) => {
    // PUT 요청 실패 시 캐시 데이터 복구
    if (data) {
      const currentQueryKey = ['get', `/collections/${Number(collectionIdPathParam)}`];

      // 실패한 요청의 imageId 추출
      const failedImageId = Number(err.config?.url?.split('/').pop());

      // 이전 상태로 데이터 복구
      const restoredPages = data.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          data: page.data.data.map((item: IImageListItem) => {
            if (item.id === failedImageId) {
              return {
                ...item,
                isUsed: !item.isUsed, // 토글된 상태를 다시 원래대로
                usedCount: item.isUsed ? item.usedCount - 1 : item.usedCount + 1 // 변경된 usedCount를 다시 원래대로
              };
            }
            return item;
          })
        }
      }));

      // 캐시 업데이트
      queryClient.setQueryData(currentQueryKey, (oldData: any) => ({
        pageParams: oldData.pageParams,
        pages: restoredPages
      }));

      // 리렌더링 트리거
      queryClient.invalidateQueries({
        queryKey: currentQueryKey,
        exact: true,
        refetchType: 'none'
      });
    }
  });

  const { mutate: deleteCollectionMutate, isPending: isDeleteCollectionPending } = useDeleteCollection();

  const handleBreadCrumbClick = () => {
    navigate(PathnameEnum.COLLECTION_LIST_PAGE);
  };

  const flattenCollectionImageList = () => {
    const dataArrLength = data?.pages.length;
    const flattenedArr: IImageListItem[] = [];

    if (dataArrLength) {
      let i = 0;
      while (i < dataArrLength) {
        if (data.pages[i].data.data.length > 0) {
          data.pages[i].data.data.map((item: IImageListItem) => {
            flattenedArr.push({
              ...item,
              isCollected: item.isCollected ?? true // 컬렉션 이미지 리스트 페이지이므로 isCollected는 기본적으로 true. PUT /images/:id/collections 성공 이후 캐시 데이터 업데이트 하면서 isCollected값을 이미 추가한 경우에는 기존 값 유지
            });
          });
        }
        i++;
      }
      return flattenedArr;
    } else {
      return [];
    }
  };

  const handleImageClick = (imageId: number, sequenceId?: number) => {
    const currentSearch = location.search;
    const queryParams = new URLSearchParams(currentSearch);

    queryParams.set(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE, 'true');
    queryParams.set(QueryParamKeyEnum.IMAGE_ID, imageId.toString());
    if (sequenceId) {
      queryParams.set(QueryParamKeyEnum.SEQUENCE_ID, sequenceId.toString());
    }

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  const handleImageModalClose = () => {
    const currentSearch = location.search;
    const queryParams = new URLSearchParams(currentSearch);

    queryParams.delete(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE);
    queryParams.delete(QueryParamKeyEnum.IMAGE_ID);
    queryParams.delete(QueryParamKeyEnum.SEQUENCE_ID);

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  const handleImageUseButtonClick = (imageId: number, isUsed: boolean, usedCount: number) => {
    if (data) {
      // 1. useFetchCollectionImageList와 동일한 쿼리 키 사용
      const currentQueryKey = ['get', `/collections/${Number(collectionIdPathParam)}`];

      // 2. 새로운 페이지 데이터 생성
      const newPages = data.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          data: page.data.data.map((item: IImageListItem) => {
            if (item.id === imageId) {
              return {
                ...item,
                isUsed: !isUsed,
                usedCount: !isUsed ? usedCount + 1 : usedCount - 1
              };
            }
            return item;
          })
        }
      }));

      // 3. 캐시 업데이트
      queryClient.setQueryData(currentQueryKey, (oldData: any) => {
        if (!oldData) {
          return {
            pageParams: [1], // 초기 페이지 파라미터 설정
            pages: newPages
          };
        }

        return {
          pageParams: oldData.pageParams,
          pages: newPages
        };
      });

      // 4. 쿼리 무효화하여 리렌더링 트리거
      queryClient.invalidateQueries({
        queryKey: currentQueryKey,
        exact: true,
        refetchType: 'none' // 실제 API 호출은 하지 않고 캐시만 업데이트
      });

      // 5. PUT /images/used-image/:id 요청
      putImageUseMutateAsync(imageId).then(() => {
        // 요청 성공시 토스트 메세지 보여주기
        showToast(isUsed ? ToastMessageEnum.CANCEL_IMAGE_USE : ToastMessageEnum.ADD_IMAGE_USE);
      });
    }
  };

  const handleCollectionUpsertModalOpen = () => {
    setIsCollectionUpsertModalVisible(true);
  };

  const handleCollectionUpsertModalClose = () => {
    setIsCollectionUpsertModalVisible(false);
  };

  const handleCollectionDeleteModalOpen = () => {
    setIsCollectionDeleteModalVisible(true);
  };

  const handleCollectionDeleteModalClose = () => {
    setIsCollectionDeleteModalVisible(false);
  };

  const handleCollectionDeleteConfirmClick = () => {
    if (isDeleteCollectionPending) {
      return;
    }

    deleteCollectionMutate(
      { collectionId: Number(collectionIdPathParam) },
      {
        onSuccess: () => {
          navigate(PathnameEnum.COLLECTION_LIST_PAGE);
        }
      }
    );
  };

  // 컬렉션 이미지 리스트 다음 페이지 호출
  useEffect(() => {
    if (outputBottomRefCurrent) {
      const observerCallback: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          //target이 화면에 관찰되고, 다음페이지가 있고, API 요청 처리중이 아니라면 다음페이지를 호출
          if (entry.isIntersecting && hasNextPage && !isLoading && !isFetching) {
            fetchNextPage();
          }
        });
      };

      //ointersection observer 인스턴스 생성
      const observer = new IntersectionObserver(observerCallback, {
        threshold: 1.0
      });

      // 타겟 관찰 시작
      observer.observe(outputBottomRefCurrent);

      // 관찰 멈춤
      return () => observer.unobserve(outputBottomRefCurrent);
    }
  }, [outputBottomRefCurrent, hasNextPage, isLoading, isFetching, fetchNextPage]);

  return {
    handleBreadCrumbClick,
    collectionIdPathParam,
    initialCollectionName,
    data,
    flattenCollectionImageList,
    outputBottomRef,

    handleImageClick,
    handleImageModalClose,

    handleImageUseButtonClick,

    // 컬렉션 생성/수정 모달 관련
    isCollectionUpsertModalVisible,
    handleCollectionUpsertModalOpen,
    handleCollectionUpsertModalClose,

    // 컬렉션 삭제 관련
    isCollectionDeleteModalVisible,
    handleCollectionDeleteModalOpen,
    handleCollectionDeleteModalClose,
    handleCollectionDeleteConfirmClick
  };
};
